import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const EMPTY = { owner_email: "", name: "", street: "", postal_code: "", city: "", iban: "" };

export const AdminChangeOwnerDialog = ({ company, users = [], onChanged }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiClient.put(`/admin/companies/${company.id}/owner`, { owner_email: email });
      toast.success(r.data.detail);
      setOpen(false);
      setEmail("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zuordnung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900"
        onClick={() => setOpen(true)}
        data-testid={`admin-change-owner-btn-${company.id}`}
      >
        Owner ändern
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="admin-change-owner-dialog">
          <DialogHeader>
            <DialogTitle>Firma einem Kunden zuordnen</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">
              <b>{company.name}</b> — aktueller Owner: {company.owner_email || "—"}
            </p>
            <div className="space-y-1">
              <Label htmlFor="aco-email">Neue Kunden-E-Mail (Owner)</Label>
              <Input
                id="aco-email"
                type="email"
                required
                list="admin-users-emails-2"
                placeholder="kunde@firma.ch"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim())}
                data-testid="admin-change-owner-email"
              />
              <datalist id="admin-users-emails-2">
                {users.map((u) => <option key={u.id || u.email} value={u.email} />)}
              </datalist>
              <p className="text-[11px] text-slate-400">
                Existiert der Kunde noch nicht, wird er freigeschaltet und übernimmt die Firma beim ersten Login.
              </p>
            </div>
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={busy || !email} data-testid="admin-change-owner-submit">
                {busy ? "Ordne zu ..." : "Zuordnen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const AdminCreateCompanyDialog = ({ users = [], onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiClient.post("/admin/companies", form);
      toast.success(r.data.detail);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="admin-create-company-open-btn">
        <Plus className="h-3.5 w-3.5 mr-1" /> Firma für Kunde
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="admin-create-company-dialog">
          <DialogHeader>
            <DialogTitle>Firma für Kunden anlegen</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 text-sm">
            <div className="space-y-1">
              <Label htmlFor="acc-email">Kunden-E-Mail (Owner)</Label>
              <Input
                id="acc-email"
                type="email"
                required
                list="admin-users-emails"
                placeholder="kunde@firma.ch"
                value={form.owner_email}
                onChange={(e) => set("owner_email", e.target.value.trim())}
                data-testid="admin-create-company-email"
              />
              <datalist id="admin-users-emails">
                {users.map((u) => <option key={u.id || u.email} value={u.email} />)}
              </datalist>
              <p className="text-[11px] text-slate-400">
                Existiert der Kunde noch nicht, wird er automatisch freigeschaltet und übernimmt die Firma beim ersten Login.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="acc-name">Firmenname</Label>
              <Input id="acc-name" required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="admin-create-company-name" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="acc-street">Strasse + Nr.</Label>
              <Input id="acc-street" required value={form.street} onChange={(e) => set("street", e.target.value)} data-testid="admin-create-company-street" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label htmlFor="acc-plz">PLZ</Label>
                <Input id="acc-plz" required value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="admin-create-company-plz" />
              </div>
              <div className="space-y-1 col-span-2">
                <Label htmlFor="acc-city">Ort</Label>
                <Input id="acc-city" required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="admin-create-company-city" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="acc-iban">IBAN</Label>
              <Input id="acc-iban" required placeholder="CH.." value={form.iban} onChange={(e) => set("iban", e.target.value)} data-testid="admin-create-company-iban" />
            </div>
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={busy} data-testid="admin-create-company-submit">
                {busy ? "Lege an ..." : "Firma anlegen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
